import { Select, Tooltip, MenuItem, InputLabel, FormControl } from "@material-ui/core";
import { useTranslation } from "react-i18next";
import { status as statuses } from "../../../../../@types/listings/unions";
import { ChangeEvent } from "react";
import useFirestore from "hooks/useFirestore";
import { CheckedMenuItemLabel } from "components/__haim/Common/CheckedMenuItemLabel";
import { getParsedListingStatusLabel } from "utils/listings/display";
import type { Status } from "../../../../../@types/listings/unions";
import type { TransactionType } from "../../../../../@types/sharedSchema";

type ListingDialogInputStatusProps = {
  status: Status;
  loading: boolean;
  transactionType: TransactionType | null;
  isAuthorized?: boolean;
  handleChange: ({ target: { name, value } }: ChangeEvent<HTMLInputElement>) => void;
  addDefaultFeeToOwnersData: (targetStatus: Status) => void;
};

export function ListingDialogInputStatus({
  status,
  loading,
  transactionType,
  isAuthorized,
  handleChange,
  addDefaultFeeToOwnersData
}: ListingDialogInputStatusProps): JSX.Element {
  const { t: translate } = useTranslation();

  const { isAdminOfTheOrganization } = useFirestore();

  const handleStatusChange = (event: ChangeEvent<HTMLInputElement>): void => {
    const parsedStatus = event.target.value as Status;

    addDefaultFeeToOwnersData(parsedStatus);
    handleChange(event);
  };

  const handleGetLabel = (labelValue: Status): string => {
    const parsedLabel = getParsedListingStatusLabel(labelValue, transactionType);

    const translatedLabel = translate(`listingsPage.statusLabel.${parsedLabel}`);

    return translatedLabel;
  };

  const label = translate("listingsPage.tableColumns.status");

  const isRentListing = transactionType === "rent";
  const canChangeStatusWithoutAdmin = isRentListing && isAuthorized;

  const needLastDisabledItem =
    !isAdminOfTheOrganization && !canChangeStatusWithoutAdmin && ["inNegotiation", "sold"].includes(status);

  return (
    <FormControl fullWidth disabled={loading}>
      <InputLabel>{label}</InputLabel>
      <Select
        name="status"
        value={status}
        label={label}
        onChange={handleStatusChange}
        renderValue={handleGetLabel}
      >
        {statuses.map((item) => {
          const statusNeedAdminPermission =
            !isAdminOfTheOrganization && !canChangeStatusWithoutAdmin && ["inNegotiation", "sold"].includes(item);

          if (statusNeedAdminPermission)
            return (
              <Tooltip title={translate("listingsPage.adminOnly")} placement="top" key={item}>
                <span>
                  <MenuItem disabled value={item}>
                    <CheckedMenuItemLabel selected={status === item}>
                      {handleGetLabel(item)}
                    </CheckedMenuItemLabel>
                  </MenuItem>
                </span>
              </Tooltip>
            );

          return (
            <MenuItem value={item} key={item}>
              <CheckedMenuItemLabel selected={status === item}>
                {handleGetLabel(item)}
              </CheckedMenuItemLabel>
            </MenuItem>
          );
        })}
        {needLastDisabledItem && (
          <MenuItem sx={{ display: "none" }} value={status}>
            {handleGetLabel(status)}
          </MenuItem>
        )}
      </Select>
    </FormControl>
  );
}
